/**
 * ListboxExamples.tsx
 * Comprehensive examples of HeroUI Listbox component
 * 
 * Showcases all HeroUI Listbox features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Listbox, ListboxItem, ListboxSection } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge, Button } from '@enfomentis/ui';
import { Avatar } from '@heroui/react';
import { 
  TrendingUp,
  Star,
  Crown,
  Target,
  Award,
  Trophy,
  Zap,
  Settings,
  FileText,
  Copy,
  Trash2,
  Edit3,
  Plus,
  Check,
  X,
  AlertTriangle
} from 'lucide-react';

interface ActionItem {
  key: string;
  name: string;
  description?: string;
  icon?: React.ReactNode;
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
}

interface BrokerOption {
  key: string;
  name: string;
  logo: string;
  region: string;
  fees: string;
  assets: string[];
}

interface UserOption {
  key: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
  isActive: boolean;
}

interface AssetOption {
  key: string;
  name: string;
  symbol: string;
  sector: string;
  price: string;
  change: number;
}

export const ListboxExamples: React.FC = () => {
  const [selectedValues, setSelectedValues] = useState<Record<string, unknown>>({
    basic: new Set([]),
    single: new Set([]),
    multiple: new Set([]),
    actions: new Set([]),
    colored: new Set([]),
    variants: new Set([]),
    brokers: new Set([]),
    users: new Set([]),
    assets: new Set([]),
    strategies: new Set([]),
    disabled: new Set([]),
    focused: new Set([]),
    scrolling: new Set([]),
    virtualized: new Set([]),
    empty: new Set([]),
    'card-container': new Set([]),
    'bordered-container': new Set([]),
    'shadow-container': new Set([]),
    'gradient-container': new Set([]),
    'constrained-container': new Set([]),
    'custom-container': new Set([]),
    'transparent-selection': new Set([]),
    'custom-hover': new Set([])
  });

  const basicActions: ActionItem[] = [
    { key: "new", name: "New file", icon: <Plus className="w-4 h-4" /> },
    { key: "copy", name: "Copy link", icon: <Copy className="w-4 h-4" /> },
    { key: "edit", name: "Edit file", icon: <Edit3 className="w-4 h-4" /> },
    { key: "delete", name: "Delete file", icon: <Trash2 className="w-4 h-4" />, color: "danger" }
  ];

  const coloredActions: ActionItem[] = [
    { key: "save", name: "Save changes", icon: <Check className="w-4 h-4" />, color: "success" },
    { key: "cancel", name: "Cancel action", icon: <X className="w-4 h-4" />, color: "default" },
    { key: "warning", name: "Warning action", icon: <AlertTriangle className="w-4 h-4" />, color: "warning" },
    { key: "danger", name: "Danger action", icon: <Trash2 className="w-4 h-4" />, color: "danger" },
    { key: "primary", name: "Primary action", icon: <Star className="w-4 h-4" />, color: "primary" },
    { key: "secondary", name: "Secondary action", icon: <Settings className="w-4 h-4" />, color: "secondary" }
  ];

  const brokerOptions: BrokerOption[] = [
    { key: "ib", name: "Interactive Brokers", logo: "/brokers/ib.png", region: "Global", fees: "$1/trade", assets: ["Stocks", "Options", "Futures", "Forex"] },
    { key: "td", name: "TD Ameritrade", logo: "/brokers/td.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options"] },
    { key: "schwab", name: "Charles Schwab", logo: "/brokers/schwab.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options", "Mutual Funds"] },
    { key: "fidelity", name: "Fidelity", logo: "/brokers/fidelity.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options"] },
    { key: "etrade", name: "E*TRADE", logo: "/brokers/etrade.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options"] },
    { key: "webull", name: "Webull", logo: "/brokers/webull.png", region: "US", fees: "$0 stocks", assets: ["Stocks", "ETFs", "Options", "Crypto"] }
  ];

  const userOptions: UserOption[] = [
    { key: "john", name: "John Doe", email: "john@example.com", role: "Administrator", avatar: "/users/john.jpg", isActive: true },
    { key: "jane", name: "Jane Smith", email: "jane@example.com", role: "Editor", avatar: "/users/jane.jpg", isActive: true },
    { key: "bob", name: "Bob Wilson", email: "bob@example.com", role: "Viewer", avatar: "/users/bob.jpg", isActive: false },
    { key: "alice", name: "Alice Johnson", email: "alice@example.com", role: "Moderator", avatar: "/users/alice.jpg", isActive: true }
  ];

  const assetOptions: AssetOption[] = [
    { key: "aapl", name: "Apple Inc.", symbol: "AAPL", sector: "Technology", price: "$175.43", change: 2.1 },
    { key: "googl", name: "Alphabet Inc.", symbol: "GOOGL", sector: "Technology", price: "$142.56", change: 1.8 },
    { key: "msft", name: "Microsoft Corp.", symbol: "MSFT", sector: "Technology", price: "$378.85", change: -0.5 },
    { key: "amzn", name: "Amazon.com Inc.", symbol: "AMZN", sector: "Consumer Discretionary", price: "$154.23", change: 3.2 },
    { key: "tsla", name: "Tesla Inc.", symbol: "TSLA", sector: "Automotive", price: "$248.50", change: -1.2 },
    { key: "nvda", name: "NVIDIA Corp.", symbol: "NVDA", sector: "Technology", price: "$875.28", change: 4.7 }
  ];

  // Generate large dataset for virtualization example
  interface LargeDataItem {
    key: string;
    name: string;
    value: string;
    category: string;
  }

  const largeDataset: LargeDataItem[] = Array.from({ length: 1000 }, (_, i) => ({
    key: `item-${i}`,
    name: `Large Dataset Item ${i + 1}`,
    value: `Value ${i + 1}`,
    category: `Category ${Math.floor(i / 100) + 1}`
  }));

  const handleSelectionChange = (key: string, selection: unknown) => {
    setSelectedValues(prev => ({ ...prev, [key]: selection }));
  };

  const handleAction = (key: React.Key, listboxKey: string) => {
    console.log(`Action triggered: ${key} from ${listboxKey}`);
  };

  // Helper to get selected keys as Set
  const getSelectedKeysSet = (key: string): Set<string> => {
    const value = selectedValues[key];
    if (value instanceof Set) {
      return value as Set<string>;
    }
    return value ? new Set([String(value)]) : new Set();
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">Listbox Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI Listbox component with automatic theme integration
          </p>
        </div>

        {/* Basic Variants */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Listbox Variants</h2>
              <Badge color="primary" variant="flat" size="sm">6 variants</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Solid Variant</h4>
                <Listbox
                  aria-label="Solid actions"
                  variant="solid"
                  color="primary"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('solid')}
                  onSelectionChange={(keys) => handleSelectionChange('solid', keys)}
                  onAction={(key) => handleAction(key, 'solid')}
                >
                  <ListboxItem key="solid1" textValue="Solid Option 1">Solid Option 1</ListboxItem>
                  <ListboxItem key="solid2" textValue="Solid Option 2">Solid Option 2</ListboxItem>
                  <ListboxItem key="solid3" textValue="Solid Option 3">Solid Option 3</ListboxItem>
                </Listbox>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Bordered Variant</h4>
                <Listbox
                  aria-label="Bordered actions"
                  variant="bordered"
                  color="secondary"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('bordered')}
                  onSelectionChange={(keys) => handleSelectionChange('bordered', keys)}
                  onAction={(key) => handleAction(key, 'bordered')}
                >
                  <ListboxItem key="bordered1" textValue="Bordered Option 1">Bordered Option 1</ListboxItem>
                  <ListboxItem key="bordered2" textValue="Bordered Option 2">Bordered Option 2</ListboxItem>
                  <ListboxItem key="bordered3" textValue="Bordered Option 3">Bordered Option 3</ListboxItem>
                </Listbox>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Light Variant</h4>
                <Listbox
                  aria-label="Light actions"
                  variant="light"
                  color="success"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('light')}
                  onSelectionChange={(keys) => handleSelectionChange('light', keys)}
                  onAction={(key) => handleAction(key, 'light')}
                >
                  <ListboxItem key="light1" textValue="Light Option 1">Light Option 1</ListboxItem>
                  <ListboxItem key="light2" textValue="Light Option 2">Light Option 2</ListboxItem>
                  <ListboxItem key="light3" textValue="Light Option 3">Light Option 3</ListboxItem>
                </Listbox>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Flat Variant</h4>
                <Listbox
                  aria-label="Flat actions"
                  variant="flat"
                  color="warning"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('flat')}
                  onSelectionChange={(keys) => handleSelectionChange('flat', keys)}
                  onAction={(key) => handleAction(key, 'flat')}
                >
                  <ListboxItem key="flat1" textValue="Flat Option 1">Flat Option 1</ListboxItem>
                  <ListboxItem key="flat2" textValue="Flat Option 2">Flat Option 2</ListboxItem>
                  <ListboxItem key="flat3" textValue="Flat Option 3">Flat Option 3</ListboxItem>
                </Listbox>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Faded Variant</h4>
                <Listbox
                  aria-label="Faded actions"
                  variant="faded"
                  color="danger"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('faded')}
                  onSelectionChange={(keys) => handleSelectionChange('faded', keys)}
                  onAction={(key) => handleAction(key, 'faded')}
                >
                  <ListboxItem key="faded1" textValue="Faded Option 1">Faded Option 1</ListboxItem>
                  <ListboxItem key="faded2" textValue="Faded Option 2">Faded Option 2</ListboxItem>
                  <ListboxItem key="faded3" textValue="Faded Option 3">Faded Option 3</ListboxItem>
                </Listbox>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Shadow Variant</h4>
                <Listbox
                  aria-label="Shadow actions"
                  variant="shadow"
                  color="default"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('shadow')}
                  onSelectionChange={(keys) => handleSelectionChange('shadow', keys)}
                  onAction={(key) => handleAction(key, 'shadow')}
                >
                  <ListboxItem key="shadow1" textValue="Shadow Option 1">Shadow Option 1</ListboxItem>
                  <ListboxItem key="shadow2" textValue="Shadow Option 2">Shadow Option 2</ListboxItem>
                  <ListboxItem key="shadow3" textValue="Shadow Option 3">Shadow Option 3</ListboxItem>
                </Listbox>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Selection Modes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Selection Modes</h2>
              <Badge color="success" variant="flat" size="sm">Single & Multiple</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Single Selection</h4>
                <Listbox
                  aria-label="Single selection actions"
                  variant="bordered"
                  color="primary"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('single')}
                  onSelectionChange={(keys) => handleSelectionChange('single', keys)}
                  onAction={(key) => handleAction(key, 'single')}
                >
                  {basicActions.map((action) => (
                    <ListboxItem 
                      key={action.key}
                      startContent={action.icon}
                      color={action.color}
                      textValue={action.name}
                    >
                      {action.name}
                    </ListboxItem>
                  ))}
                </Listbox>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Multiple Selection</h4>
                <Listbox
                  aria-label="Multiple selection actions"
                  variant="bordered"
                  color="secondary"
                  selectionMode="multiple"
                  selectedKeys={getSelectedKeysSet('multiple')}
                  onSelectionChange={(keys) => handleSelectionChange('multiple', keys)}
                  onAction={(key) => handleAction(key, 'multiple')}
                >
                  {coloredActions.map((action) => (
                    <ListboxItem 
                      key={action.key}
                      startContent={action.icon}
                      color={action.color}
                      textValue={action.name}
                    >
                      {action.name}
                    </ListboxItem>
                  ))}
                </Listbox>
              </div>
            </div>
            
            <div className="bg-default-100 p-3 rounded-lg">
              <p className="text-sm text-default-600">
                <strong>Selected Single:</strong> {Array.from(getSelectedKeysSet('single')).join(', ') || 'None'}<br />
                <strong>Selected Multiple:</strong> {Array.from(getSelectedKeysSet('multiple')).join(', ') || 'None'}
              </p>
            </div>
          </CardBody>
        </Card>

        {/* Colors */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Color Variants</h2>
              <Badge color="danger" variant="flat" size="sm">Theme colors</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(['default', 'primary', 'secondary', 'success', 'warning', 'danger'] as const).map((color) => (
                <div key={color} className="space-y-2">
                  <h4 className="text-sm font-medium capitalize">{color} Color</h4>
                  <Listbox
                    aria-label={`${color} actions`}
                    variant="bordered"
                    color={color}
                    selectionMode="single"
                    selectedKeys={getSelectedKeysSet(`color-${color}`)}
                    onSelectionChange={(keys) => handleSelectionChange(`color-${color}`, keys)}
                  >
                    <ListboxItem key={`${color}1`} startContent={<Star className="w-4 h-4" />} textValue={`${color} Option 1`}>
                      {color} Option 1
                    </ListboxItem>
                    <ListboxItem key={`${color}2`} startContent={<Target className="w-4 h-4" />} textValue={`${color} Option 2`}>
                      {color} Option 2
                    </ListboxItem>
                  </Listbox>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Listbox with Sections */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Grouped Options</h2>
              <Badge color="secondary" variant="flat" size="sm">Sections</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <Listbox
                aria-label="Trading strategies"
                variant="bordered"
                color="primary"
                selectionMode="multiple"
                selectedKeys={getSelectedKeysSet('strategies')}
                onSelectionChange={(keys) => handleSelectionChange('strategies', keys)}
                onAction={(key) => handleAction(key, 'strategies')}
                topContent={
                  <div className="px-2 py-1 text-small text-default-500">
                    Choose your trading strategies
                  </div>
                }
                bottomContent={
                  <div className="px-2 py-1 text-small text-default-400">
                    {Array.from(getSelectedKeysSet('strategies')).length} strategies selected
                  </div>
                }
              >
                <ListboxSection title="Conservative Strategies" showDivider>
                  <ListboxItem key="buy-hold" startContent={<Target className="w-4 h-4" />} textValue="Buy and Hold">
                    Buy and Hold
                  </ListboxItem>
                  <ListboxItem key="dca" startContent={<TrendingUp className="w-4 h-4" />} textValue="Dollar Cost Averaging">
                    Dollar Cost Averaging
                  </ListboxItem>
                  <ListboxItem key="dividend" startContent={<Award className="w-4 h-4" />} textValue="Dividend Investing">
                    Dividend Investing
                  </ListboxItem>
                </ListboxSection>
                
                <ListboxSection title="Moderate Strategies" showDivider>
                  <ListboxItem key="swing" startContent={<Star className="w-4 h-4" />} textValue="Swing Trading">
                    Swing Trading
                  </ListboxItem>
                  <ListboxItem key="momentum" startContent={<Zap className="w-4 h-4" />} textValue="Momentum Trading">
                    Momentum Trading
                  </ListboxItem>
                  <ListboxItem key="covered-calls" startContent={<Crown className="w-4 h-4" />} textValue="Covered Calls">
                    Covered Calls
                  </ListboxItem>
                </ListboxSection>
                
                <ListboxSection title="Aggressive Strategies">
                  <ListboxItem key="day-trading" startContent={<Trophy className="w-4 h-4" />} textValue="Day Trading">
                    Day Trading
                  </ListboxItem>
                  <ListboxItem key="scalping" startContent={<Zap className="w-4 h-4" />} textValue="Scalping">
                    Scalping
                  </ListboxItem>
                  <ListboxItem key="options" startContent={<Target className="w-4 h-4" />} textValue="Options Trading">
                    Options Trading
                  </ListboxItem>
                </ListboxSection>
              </Listbox>
            </div>
          </CardBody>
        </Card>

        {/* Rich Content */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Rich Content</h2>
              <Badge variant="flat" size="sm">Advanced</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Brokers with descriptions */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Broker Selection</h4>
                <Listbox
                  aria-label="Select broker"
                  variant="bordered"
                  color="primary"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('brokers')}
                  onSelectionChange={(keys) => handleSelectionChange('brokers', keys)}
                  onAction={(key) => handleAction(key, 'brokers')}
                  className="max-h-64"
                >
                  {brokerOptions.map((broker) => (
                    <ListboxItem 
                      key={broker.key}
                      description={`${broker.region} • ${broker.fees} • ${broker.assets.join(", ")}`}
                      startContent={
                        <div className="w-8 h-8 bg-default-200 rounded-full flex items-center justify-center text-sm font-semibold">
                          {broker.name.charAt(0)}
                        </div>
                      }
                      textValue={broker.name}
                    >
                      {broker.name}
                    </ListboxItem>
                  ))}
                </Listbox>
              </div>

              {/* Users with avatars and status */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">User Assignment</h4>
                <Listbox
                  aria-label="Assign user"
                  variant="bordered"
                  color="secondary"
                  selectionMode="multiple"
                  selectedKeys={getSelectedKeysSet('users')}
                  onSelectionChange={(keys) => handleSelectionChange('users', keys)}
                  onAction={(key) => handleAction(key, 'users')}
                  disabledKeys={userOptions.filter(user => !user.isActive).map(user => user.key)}
                >
                  {userOptions.map((user) => (
                    <ListboxItem 
                      key={user.key}
                      description={`${user.role} • ${user.email} ${!user.isActive ? '(Inactive)' : ''}`}
                      startContent={
                        <Avatar 
                          src={user.avatar} 
                          name={user.name}
                          size="sm" 
                          className="w-6 h-6"
                        />
                      }
                      endContent={
                        user.isActive ? (
                          <Badge color="success" variant="flat" size="sm">Active</Badge>
                        ) : (
                          <Badge color="default" variant="flat" size="sm">Inactive</Badge>
                        )
                      }
                      textValue={user.name}
                    >
                      {user.name}
                    </ListboxItem>
                  ))}
                </Listbox>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Advanced Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Advanced Features</h2>
              <Badge variant="flat" size="sm">Interactive</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Focus management */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Focus Management</h4>
                <Listbox
                  aria-label="Focus management"
                  variant="bordered"
                  color="success"
                  selectionMode="single"
                  autoFocus={true}
                  shouldFocusWrap={true}
                  shouldHighlightOnFocus={true}
                  selectedKeys={getSelectedKeysSet('focused')}
                  onSelectionChange={(keys) => handleSelectionChange('focused', keys)}
                  onAction={(key) => handleAction(key, 'focused')}
                >
                  <ListboxItem key="first" startContent={<Check className="w-4 h-4" />} textValue="First Item (Auto-focused)">
                    First Item (Auto-focused)
                  </ListboxItem>
                  <ListboxItem key="second" startContent={<FileText className="w-4 h-4" />} textValue="Second Item">
                    Second Item
                  </ListboxItem>
                  <ListboxItem key="third" startContent={<Settings className="w-4 h-4" />} textValue="Third Item">
                    Third Item
                  </ListboxItem>
                  <ListboxItem key="last" startContent={<Target className="w-4 h-4" />} textValue="Last Item (Wraps to first)">
                    Last Item (Wraps to first)
                  </ListboxItem>
                </Listbox>
              </div>

              {/* Disabled items */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Disabled Items</h4>
                <Listbox
                  aria-label="Actions with disabled items"
                  variant="bordered"
                  color="warning"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('disabled')}
                  onSelectionChange={(keys) => handleSelectionChange('disabled', keys)}
                  onAction={(key) => handleAction(key, 'disabled')}
                  disabledKeys={["disabled1", "disabled3"]}
                  disallowEmptySelection={true}
                >
                  <ListboxItem key="enabled1" startContent={<Check className="w-4 h-4 text-success" />} textValue="Enabled Option 1">
                    Enabled Option 1
                  </ListboxItem>
                  <ListboxItem key="disabled1" startContent={<X className="w-4 h-4 text-default-300" />} textValue="Disabled Option 1">
                    Disabled Option 1
                  </ListboxItem>
                  <ListboxItem key="enabled2" startContent={<Check className="w-4 h-4 text-success" />} textValue="Enabled Option 2">
                    Enabled Option 2
                  </ListboxItem>
                  <ListboxItem key="disabled3" startContent={<X className="w-4 h-4 text-default-300" />} textValue="Disabled Option 3">
                    Disabled Option 3
                  </ListboxItem>
                </Listbox>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Scrolling and Virtualization Examples */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Scrolling & Virtualization</h2>
              <Badge color="warning" variant="flat" size="sm">Large datasets</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Basic Scrolling Listbox */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Basic Scrolling (200 items)</h4>
                <div className="border border-border rounded-lg p-2">
                  <Listbox
                    aria-label="Scrolling listbox"
                    variant="flat"
                    color="secondary"
                    selectionMode="multiple"
                    items={largeDataset.slice(0, 200)}
                    selectedKeys={getSelectedKeysSet('scrolling')}
                    onSelectionChange={(keys) => handleSelectionChange('scrolling', keys)}
                    className="max-h-48 overflow-y-auto"
                    topContent={
                      <div className="px-2 py-1 text-small text-default-500 border-b border-divider">
                        📜 Scroll through 200 items
                      </div>
                    }
                    bottomContent={
                      <div className="px-2 py-1 text-small text-default-400 border-t border-divider">
                        {Array.from(getSelectedKeysSet('scrolling')).length} selected
                      </div>
                    }
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {(item: any) => (
                      <ListboxItem key={item.key} textValue={item.name}>
                        <div className="flex justify-between items-center">
                          <span className="text-small">{item.name}</span>
                          <span className="text-tiny text-default-400">{item.category}</span>
                        </div>
                      </ListboxItem>
                    )}
                  </Listbox>
                </div>
              </div>

              {/* Virtualized Listbox */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Virtualized (1000 items)</h4>
                <div className="border border-border rounded-lg p-2">
                  <Listbox
                    aria-label="Virtualized large dataset"
                    variant="bordered"
                    color="primary"
                    selectionMode="multiple"
                    items={largeDataset}
                    isVirtualized
                    virtualization={{
                      maxListboxHeight: 192, // max-h-48
                      itemHeight: 40
                    }}
                    selectedKeys={getSelectedKeysSet('virtualized')}
                    onSelectionChange={(keys) => handleSelectionChange('virtualized', keys)}
                    topContent={
                      <div className="px-2 py-1 text-small text-default-500 border-b border-divider">
                        ⚡ Virtualized 1000 items
                      </div>
                    }
                    bottomContent={
                      <div className="px-2 py-1 text-small text-default-400 border-t border-divider">
                        {Array.from(getSelectedKeysSet('virtualized')).length} of 1000 selected
                      </div>
                    }
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {(item: any) => (
                      <ListboxItem key={item.key} textValue={item.name}>
                        <div className="flex justify-between items-center min-h-[40px]">
                          <div className="flex flex-col">
                            <span className="text-small font-medium">{item.name}</span>
                            <span className="text-tiny text-default-400">{item.category}</span>
                          </div>
                          <Badge size="sm" variant="flat" color="primary">
                            #{item.key.split('-')[1]}
                          </Badge>
                        </div>
                      </ListboxItem>
                    )}
                  </Listbox>
                </div>
              </div>
            </div>

            {/* Performance Comparison */}
            <div className="bg-default-50 p-4 rounded-lg">
              <h5 className="text-sm font-semibold mb-2 text-default-700">Performance Comparison</h5>
              <div className="text-xs text-default-600 space-y-1">
                <p><strong>Basic Scrolling:</strong> All 200 items rendered in DOM - Good for smaller lists</p>
                <p><strong>Virtualized:</strong> Only visible items rendered - Efficient for 1000+ items</p>
                <p><strong>Tip:</strong> Use virtualization when you have more than ~500 items for better performance</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Bounding Box Examples */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Bounding Box Examples</h2>
              <Badge color="secondary" variant="flat" size="sm">Containers</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* Card Container */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Card Container</h4>
                <Card className="p-0 shadow-medium">
                  <CardBody className="p-0">
                    <Listbox
                      aria-label="Card container listbox"
                      variant="flat"
                      color="primary"
                      selectionMode="single"
                      selectedKeys={getSelectedKeysSet('card-container')}
                      onSelectionChange={(keys) => handleSelectionChange('card-container', keys)}
                      className="rounded-lg"
                    >
                      <ListboxItem key="card1" textValue="Card Item 1">Card Item 1</ListboxItem>
                      <ListboxItem key="card2" textValue="Card Item 2">Card Item 2</ListboxItem>
                      <ListboxItem key="card3" textValue="Card Item 3">Card Item 3</ListboxItem>
                    </Listbox>
                  </CardBody>
                </Card>
              </div>

              {/* Bordered Container */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Thick Border</h4>
                <div className="border-4 border-primary rounded-xl p-3 bg-primary/5">
                  <Listbox
                    aria-label="Bordered container listbox"
                    variant="light"
                    color="primary"
                    selectionMode="single"
                    selectedKeys={getSelectedKeysSet('bordered-container')}
                    onSelectionChange={(keys) => handleSelectionChange('bordered-container', keys)}
                  >
                    <ListboxItem key="border1" textValue="Bordered Item 1">Bordered Item 1</ListboxItem>
                    <ListboxItem key="border2" textValue="Bordered Item 2">Bordered Item 2</ListboxItem>
                    <ListboxItem key="border3" textValue="Bordered Item 3">Bordered Item 3</ListboxItem>
                  </Listbox>
                </div>
              </div>

              {/* Shadow Container */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Shadow Container</h4>
                <div className="shadow-2xl rounded-2xl p-4 bg-background border border-border">
                  <Listbox
                    aria-label="Shadow container listbox"
                    variant="flat"
                    color="secondary"
                    selectionMode="single"
                    selectedKeys={getSelectedKeysSet('shadow-container')}
                    onSelectionChange={(keys) => handleSelectionChange('shadow-container', keys)}
                  >
                    <ListboxItem key="shadow1" textValue="Shadow Item 1">Shadow Item 1</ListboxItem>
                    <ListboxItem key="shadow2" textValue="Shadow Item 2">Shadow Item 2</ListboxItem>
                    <ListboxItem key="shadow3" textValue="Shadow Item 3">Shadow Item 3</ListboxItem>
                  </Listbox>
                </div>
              </div>

              {/* Gradient Container */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Gradient Background</h4>
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-200 rounded-xl p-3">
                  <Listbox
                    aria-label="Gradient container listbox"
                    variant="flat"
                    color="secondary"
                    selectionMode="single"
                    selectedKeys={getSelectedKeysSet('gradient-container')}
                    onSelectionChange={(keys) => handleSelectionChange('gradient-container', keys)}
                  >
                    <ListboxItem key="grad1" textValue="Gradient Item 1">Gradient Item 1</ListboxItem>
                    <ListboxItem key="grad2" textValue="Gradient Item 2">Gradient Item 2</ListboxItem>
                    <ListboxItem key="grad3" textValue="Gradient Item 3">Gradient Item 3</ListboxItem>
                  </Listbox>
                </div>
              </div>

              {/* Constrained Height with Scroll */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Constrained Height</h4>
                <div className="h-32 border-2 border-dashed border-warning rounded-lg p-2 bg-warning/5">
                  <Listbox
                    aria-label="Constrained height listbox"
                    variant="bordered"
                    color="warning"
                    selectionMode="multiple"
                    selectedKeys={getSelectedKeysSet('constrained-container')}
                    onSelectionChange={(keys) => handleSelectionChange('constrained-container', keys)}
                    className="h-full overflow-y-auto"
                  >
                    <ListboxItem key="const1" textValue="Constrained 1">Constrained 1</ListboxItem>
                    <ListboxItem key="const2" textValue="Constrained 2">Constrained 2</ListboxItem>
                    <ListboxItem key="const3" textValue="Constrained 3">Constrained 3</ListboxItem>
                    <ListboxItem key="const4" textValue="Constrained 4">Constrained 4</ListboxItem>
                    <ListboxItem key="const5" textValue="Constrained 5">Constrained 5</ListboxItem>
                    <ListboxItem key="const6" textValue="Constrained 6">Constrained 6</ListboxItem>
                  </Listbox>
                </div>
              </div>

              {/* Custom Styled Container */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Custom Styled</h4>
                <div 
                  className="relative rounded-2xl p-4 border border-success/30"
                  style={{
                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <div className="absolute inset-0 rounded-2xl border border-white/20 pointer-events-none"></div>
                  <Listbox
                    aria-label="Custom styled listbox"
                    variant="light"
                    color="success"
                    selectionMode="single"
                    selectedKeys={getSelectedKeysSet('custom-container')}
                    onSelectionChange={(keys) => handleSelectionChange('custom-container', keys)}
                  >
                    <ListboxItem key="custom1" textValue="Custom Item 1">Custom Item 1</ListboxItem>
                    <ListboxItem key="custom2" textValue="Custom Item 2">Custom Item 2</ListboxItem>
                    <ListboxItem key="custom3" textValue="Custom Item 3">Custom Item 3</ListboxItem>
                  </Listbox>
                </div>
              </div>
            </div>

            {/* Container Tips */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h5 className="text-sm font-semibold mb-2 text-blue-800">Container Styling Tips</h5>
              <div className="text-xs text-blue-700 space-y-1">
                <p><strong>Cards:</strong> Use Card components for elevated containers with built-in shadows</p>
                <p><strong>Borders:</strong> Apply border utilities or custom border styles to create distinct boundaries</p>
                <p><strong>Heights:</strong> Set explicit heights with overflow for scrollable containers</p>
                <p><strong>Backgrounds:</strong> Use gradients or colored backgrounds to create visual hierarchy</p>
                <p><strong>Custom:</strong> Combine multiple styling techniques for unique container designs</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Assets Example */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Dynamic Assets</h2>
              <Badge color="success" variant="flat" size="sm">Real-time data</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <Listbox
                aria-label="Select trading assets"
                variant="bordered"
                color="success"
                selectionMode="multiple"
                items={assetOptions}
                selectedKeys={getSelectedKeysSet('assets')}
                onSelectionChange={(keys) => handleSelectionChange('assets', keys)}
                onAction={(key) => handleAction(key, 'assets')}
                topContent={
                  <div className="px-2 py-1 text-small text-default-500 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Select assets to trade
                  </div>
                }
                bottomContent={
                  <div className="px-2 py-1 text-small text-default-400">
                    {Array.from(getSelectedKeysSet('assets')).length} assets selected
                  </div>
                }
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(asset: any) => (
                  <ListboxItem 
                    key={asset.key}
                    textValue={`${asset.symbol} ${asset.name}`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex flex-col">
                        <span className="text-small font-semibold">{asset.symbol}</span>
                        <span className="text-tiny text-default-500">{asset.name}</span>
                        <span className="text-tiny text-default-400">{asset.sector}</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-small font-semibold">{asset.price}</span>
                        <span className={`text-tiny ${asset.change >= 0 ? 'text-success' : 'text-danger'}`}>
                          {asset.change >= 0 ? '+' : ''}{asset.change}%
                        </span>
                      </div>
                    </div>
                  </ListboxItem>
                )}
              </Listbox>
            </div>
          </CardBody>
        </Card>

        {/* Custom Selection Styling */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Custom Selection Styling</h2>
              <Badge color="primary" variant="flat" size="sm">Advanced</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Transparent Selection */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Transparent Selection (Checkmarks Only)</h4>
                <Listbox
                  aria-label="Transparent selection styling"
                  variant="bordered"
                  color="primary"
                  selectionMode="multiple"
                  selectedKeys={getSelectedKeysSet('transparent-selection')}
                  onSelectionChange={(keys) => handleSelectionChange('transparent-selection', keys)}
                  className="[&_[data-selected=true]]:!bg-transparent [&_[aria-selected=true]]:!bg-transparent [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
                >
                  <ListboxItem 
                    key="trans1" 
                    textValue="Item 1" 
                    className="data-[hover=true]:bg-default-100"
                    startContent={<div className="w-2 h-2 rounded-full bg-success"></div>}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">Item 1</span>
                      <span className="text-xs text-success-600">Available</span>
                    </div>
                  </ListboxItem>
                  <ListboxItem 
                    key="trans2" 
                    textValue="Item 2" 
                    className="data-[hover=true]:bg-default-100"
                    startContent={<div className="w-2 h-2 rounded-full bg-warning"></div>}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">Item 2</span>
                      <span className="text-xs text-warning-600">Already exists</span>
                    </div>
                  </ListboxItem>
                  <ListboxItem 
                    key="trans3" 
                    textValue="Item 3" 
                    className="data-[hover=true]:bg-default-100"
                    startContent={<div className="w-2 h-2 rounded-full bg-success"></div>}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">Item 3</span>
                      <span className="text-xs text-success-600">New item</span>
                    </div>
                  </ListboxItem>
                </Listbox>
              </div>

              {/* Custom Hover Colors */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Custom Hover Colors</h4>
                <Listbox
                  aria-label="Custom hover styling"
                  variant="bordered"
                  color="secondary"
                  selectionMode="single"
                  selectedKeys={getSelectedKeysSet('custom-hover')}
                  onSelectionChange={(keys) => handleSelectionChange('custom-hover', keys)}
                >
                  <ListboxItem 
                    key="hover1" 
                    textValue="Hover Primary" 
                    className="data-[hover=true]:bg-primary-50 data-[hover=true]:text-primary"
                  >
                    Hover Primary (Blue)
                  </ListboxItem>
                  <ListboxItem 
                    key="hover2" 
                    textValue="Hover Success" 
                    className="data-[hover=true]:bg-success-50 data-[hover=true]:text-success"
                  >
                    Hover Success (Green)
                  </ListboxItem>
                  <ListboxItem 
                    key="hover3" 
                    textValue="Hover Warning" 
                    className="data-[hover=true]:bg-warning-50 data-[hover=true]:text-warning-600"
                  >
                    Hover Warning (Orange)
                  </ListboxItem>
                </Listbox>
              </div>
            </div>

            {/* Styling Tips */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h5 className="text-sm font-semibold mb-2 text-blue-800">Selection Styling Tips</h5>
              <div className="text-xs text-blue-700 space-y-1">
                <p><strong>Transparent Selection:</strong> Use Listbox className with <code className="bg-blue-100 px-1 rounded">[&_[data-selected=true]]:!bg-transparent</code> to remove selection highlighting</p>
                <p><strong>Custom Hover:</strong> Apply ListboxItem className with <code className="bg-blue-100 px-1 rounded">data-[hover=true]:bg-color</code> for custom hover colors</p>
                <p><strong>Checkmarks Only:</strong> Combine transparent selection with visible checkmarks for minimal selection feedback</p>
                <p><strong>Pattern:</strong> Listbox controls selection colors, ListboxItem controls hover colors</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Empty State */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Empty State</h2>
              <Badge color="default" variant="flat" size="sm">No items</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-md">
              <Listbox
                aria-label="Empty listbox"
                variant="bordered"
                color="default"
                selectionMode="single"
                items={[]}
                selectedKeys={getSelectedKeysSet('empty')}
                onSelectionChange={(keys) => handleSelectionChange('empty', keys)}
                hideEmptyContent={false}
                emptyContent={
                  <div className="text-center py-8">
                    <div className="text-default-400 text-large mb-2">
                      <FileText className="w-8 h-8 mx-auto mb-2" />
                    </div>
                    <p className="text-default-500 text-small mb-4">No items found</p>
                    <Button size="sm" color="primary" className="mt-2">
                      Add New Item
                    </Button>
                  </div>
                }
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(item: any) => <ListboxItem key={item.id} textValue={item.name}>{item.name}</ListboxItem>}
              </Listbox>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};